import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, WarningCircle, ArrowRight } from "@phosphor-icons/react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    const poll = async () => {
      while (!cancelled && tries.current < 10) {
        tries.current += 1;
        try {
          const { data } = await api.get(`/billing/status/${sessionId}`);
          if (data.payment_status === "paid") { setStatus("paid"); return; }
          if (data.status === "expired") { setStatus("failed"); return; }
        } catch {/* keep trying */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setStatus("failed");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center p-6">
      <Card className="rounded-3xl border-[#E3E1D9] max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === "checking" && (
            <>
              <div className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-[#2C4C3B] border-t-transparent animate-spin" />
              <h2 className="font-[Manrope] text-2xl font-bold">Confirming your subscription…</h2>
              <p className="text-[#5C6660] mt-2 text-sm">Just a moment.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle weight="duotone" size={56} className="text-[#2C4C3B] mx-auto" />
              <h2 className="font-[Manrope] text-3xl font-bold mt-4">You're in 🎉</h2>
              <p className="text-[#5C6660] mt-2">Your MedEasy subscription is active. 10% of your payment is set aside for Hope Air.</p>
              <Button
                data-testid="billing-success-continue"
                onClick={() => navigate("/log")}
                className="mt-6 rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white h-11 px-6"
              >
                Open MedEasy <ArrowRight size={18} className="ml-2" />
              </Button>
            </>
          )}
          {status === "failed" && (
            <>
              <WarningCircle weight="duotone" size={56} className="text-[#C17767] mx-auto" />
              <h2 className="font-[Manrope] text-2xl font-bold mt-4">We couldn't confirm payment</h2>
              <p className="text-[#5C6660] mt-2 text-sm">If you were charged it'll show up shortly. Try again or contact support.</p>
              <Button onClick={() => navigate("/subscribe")} variant="outline" className="mt-6 rounded-full h-11 px-6">Back to subscribe</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
